// Period picker modal — month picker used to generate a report for a period.
// Extracted from Reports.tsx during Maintainability Sprint 2026-04-19
// (Task 1.2 step 3). See docs/superpowers/plans/.
import { useCallback, useState } from "react";
import { Button } from "@cieda/djayma-ui";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import type { ReportTemplate } from "@/lib/admin-api/finance";

interface PeriodModalProps {
  readonly open: boolean;
  readonly template: ReportTemplate | null;
  readonly onClose: () => void;
  readonly onGenerate: (templateId: string, period: string) => Promise<void>;
  readonly isGenerating: boolean;
}

export default function PeriodModal({
  open,
  template,
  onClose,
  onGenerate,
  isGenerating,
}: PeriodModalProps) {
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleGenerate = useCallback(async () => {
    if (!period || !template) {
      setError("Veuillez selectionner une periode.");
      return;
    }
    setError(null);
    try {
      await onGenerate(template.id, period);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [period, template, onGenerate, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={`Generer : ${template?.name ?? "Rapport"}`}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isGenerating}>
            Annuler
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating} className="gap-1.5">
            {isGenerating && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Generer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-stone-600">{template?.description}</p>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Periode (AAAA-MM) <span className="text-red-500">*</span>
          </label>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}
